import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { FaKeyboard, FaFileUpload, FaLink, FaArrowLeft } from "react-icons/fa";
import { BsFillArrowRightCircleFill } from "react-icons/bs";
import { GoogleAnalyticsContext } from "../../../../Hooks/GoogleAnalyticsContext";
import { useAuthenticatedFetch } from "../../../../Hooks/useFetch";
import config from "../../../../config";
import { toast } from "react-hot-toast";
import DocumentUploader from "../../DocumentUploader/DocumentUploader";
import { YippityUserContext } from "../../../../Hooks/YippityUserContext";
import useCanUsePrompt from "../../../../Hooks/useCanUsePrompt";
import { Prompt, PromptInput } from "./types";

interface PromptUsageProps {
  prompt: Prompt;
  onResult: (result: any) => void;
  onBack: () => void;
}

const PromptUsage: React.FC<PromptUsageProps> = ({
  prompt,
  onResult,
  onBack,
}) => {
  const navigate = useNavigate();
  const { canUse: canUsePrompt, usageRemaining } = useCanUsePrompt();
  const { refetch } = useContext(YippityUserContext);
  const { logEvent } = useContext(GoogleAnalyticsContext);
  const [inputValues, setInputValues] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [activeSources, setActiveSources] = useState<Record<string, string>>(
    {}
  );
  const [urlInputs, setUrlInputs] = useState<Record<string, string>>({});
  const [documentContent, setDocumentContent] = useState<
    Record<string, string>
  >({});

  const { res, callAPI: executePrompt } = useAuthenticatedFetch({
    url: `${config.API_URI}/api/prompt/${prompt.id}/execute`,
    method: "POST",
  });

  useEffect(() => {
    if (res.statusCode === 200 && res.data) {
      onResult(res.data);
      setIsLoading(false);
    } else if (res.statusCode === 500) {
      toast.error("Failed to execute custom tool");
      setIsLoading(false);
    }
  }, [res, onResult]);

  const handleInputChange = (input: PromptInput, value: any) => {
    setInputValues({
      ...inputValues,
      [input.name]: value,
    });
  };

  const handleSourceChange = (inputName: string, source: string) => {
    setActiveSources((prev) => ({
      ...prev,
      [inputName]: source,
    }));
  };

  const handleUrlInputChange = (inputName: string, url: string) => {
    setUrlInputs((prev) => ({
      ...prev,
      [inputName]: url,
    }));
  };

  const validateInputs = () => {
    const missingRequired = prompt.inputs
      .filter((input) => input.required)
      .filter((input) => {
        const activeSource = activeSources[input.name] || "direct";
        if (activeSource === "url") {
          return !urlInputs[input.name];
        }
        if (activeSource === "document") {
          return !documentContent[input.name];
        }
        return !inputValues[input.name];
      });

    if (missingRequired.length > 0) {
      toast.error(
        `Please provide values for: ${missingRequired
          .map((input) => input.name)
          .join(", ")}`
      );
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!canUsePrompt) {
      logEvent(
        "CustomToolAction",
        "UseTool",
        `tool_${prompt.id}_usage_limit_reached`
      );
      toast.error(
        `You've reached the maximum number of prompt uses (${usageRemaining} remaining). Please upgrade to continue.`
      );
      return navigate("/pricing");
    }

    if (!validateInputs()) {
      logEvent(
        "CustomToolAction",
        "UseTool",
        `tool_${prompt.id}_validation_failed`
      );
      return;
    }

    setIsLoading(true);
    try {
      // Process input values to ensure they're in the correct format
      const processedValues = Object.entries(inputValues).reduce(
        (acc, [key, value]) => {
          // Convert empty strings to null
          if (value === "") {
            acc[key] = null;
          } else {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, any>
      );

      // Handle source mappings
      prompt.inputs.forEach((input) => {
        const activeSource = activeSources[input.name] || "direct";
        const renderPrefs =
          typeof input.renderPreferences === "string"
            ? JSON.parse(input.renderPreferences)
            : input.renderPreferences || {
                renderType: input.type,
                allowedSources: ["direct"],
              };

        // If we have a URL input, use the source mapping format
        if (activeSource === "url" && urlInputs[input.name]) {
          // Find the URL source mapping
          const urlMapping = input.sourceMappings?.find(
            (mapping) => mapping.processorName === "urlProcessor"
          );

          if (urlMapping) {
            // Use the source field name from the mapping
            processedValues[urlMapping.sourceField] = urlInputs[input.name];
            // Remove the direct value if it exists
            delete processedValues[input.name];
          }
        }

        // If we have a document input, use the source mapping format
        if (activeSource === "document" && documentContent[input.name]) {
          // Find the document source mapping
          const docMapping = input.sourceMappings?.find(
            (mapping) => mapping.processorName === "documentProcessor"
          );

          if (docMapping) {
            // Use the source field name from the mapping
            processedValues[docMapping.sourceField] =
              documentContent[input.name];
            // Remove the direct value if it exists
            delete processedValues[input.name];
          }
        }
      });
      await executePrompt(JSON.stringify({ inputs: processedValues }));
      await refetch();
      logEvent("CustomToolAction", "UseTool", `tool_${prompt.id}_success`);
    } catch (error) {
      toast.error("Failed to execute custom tool");
      logEvent("CustomToolAction", "UseTool", `tool_${prompt.id}_error`);
      setIsLoading(false);
    }
  };

  const renderInput = (input: PromptInput) => {
    // Parse renderPreferences if it's a string
    const renderPrefs =
      typeof input.renderPreferences === "string"
        ? JSON.parse(input.renderPreferences)
        : input.renderPreferences || {
            renderType: input.type,
            allowedSources: ["direct"],
          };

    const { renderType, allowedSources } = renderPrefs;
    const activeSource = activeSources[input.name] || "direct";
    const showSourceSelector = allowedSources && allowedSources.length > 1;

    const renderSourceSelector = () => {
      if (!showSourceSelector) return null;

      return (
        <div className="flex mb-4 border-b border-gray-200">
          {allowedSources.includes("direct") && (
            <button
              className={`py-2 px-4 font-medium flex items-center ${
                activeSource === "direct"
                  ? "text-yellow-500 border-b-2 border-yellow-500"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => {
                handleSourceChange(input.name, "direct");
                logEvent(
                  "CustomToolInput",
                  "ChangeSource",
                  `tool_${prompt.id}_input_${input.name}_direct`
                );
              }}
            >
              <FaKeyboard className="mr-2" /> Text Input
            </button>
          )}

          {allowedSources.includes("document") && (
            <button
              className={`py-2 px-4 font-medium flex items-center ${
                activeSource === "document"
                  ? "text-yellow-500 border-b-2 border-yellow-500"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => {
                handleSourceChange(input.name, "document");
                logEvent(
                  "CustomToolInput",
                  "ChangeSource",
                  `tool_${prompt.id}_input_${input.name}_document`
                );
              }}
            >
              <FaFileUpload className="mr-2" /> Document
            </button>
          )}

          {allowedSources.includes("url") && (
            <button
              className={`py-2 px-4 font-medium flex items-center ${
                activeSource === "url"
                  ? "text-yellow-500 border-b-2 border-yellow-500"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => {
                handleSourceChange(input.name, "url");
                logEvent(
                  "CustomToolInput",
                  "ChangeSource",
                  `tool_${prompt.id}_input_${input.name}_url`
                );
              }}
            >
              <FaLink className="mr-2" /> URL
            </button>
          )}
        </div>
      );
    };

    const renderInputContent = () => {
      if (activeSource === "document") {
        return (
          <DocumentUploader
            onContentExtracted={(content: string) => {
              setDocumentContent((prev) => ({
                ...prev,
                [input.name]: content,
              }));
              handleInputChange(input, content);
            }}
            isLoading={isLoading}
          />
        );
      }

      if (activeSource === "url") {
        return (
          <div className="space-y-2">
            <div className="flex relative">
              <input
                type="url"
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent pr-10"
                placeholder="Enter URL..."
                value={urlInputs[input.name] || ""}
                onChange={(e) =>
                  handleUrlInputChange(input.name, e.target.value)
                }
              />
            </div>
            {inputValues[input.name] && (
              <div className="mt-3 p-2 bg-gray-50 rounded-md text-sm text-gray-600">
                URL submitted for processing
              </div>
            )}
          </div>
        );
      }

      // Direct input based on renderType
      switch (renderType) {
        case "textarea":
          return (
            <textarea
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              placeholder={input.description}
              value={inputValues[input.name] || ""}
              onChange={(e) => handleInputChange(input, e.target.value)}
              rows={6}
            />
          );

        case "number":
          return (
            <input
              type="number"
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              placeholder={input.description}
              value={inputValues[input.name] || ""}
              onChange={(e) => handleInputChange(input, e.target.value)}
            />
          );

        case "select":
          const options = renderPrefs.options || [];
          return (
            <select
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              value={inputValues[input.name] || ""}
              onChange={(e) => handleInputChange(input, e.target.value)}
            >
              <option value="">Select an option</option>
              {options.map((option: { label: string; value: string }) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          );

        default:
          return (
            <input
              type="text"
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              placeholder={input.description}
              value={inputValues[input.name] || ""}
              onChange={(e) => handleInputChange(input, e.target.value)}
            />
          );
      }
    };

    return (
      <div className="space-y-4">
        {renderSourceSelector()}
        {renderInputContent()}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-800"
        >
          <FaArrowLeft className="mr-2" />
          Back to My Tools
        </button>
        <h2 className="text-xl font-semibold text-gray-800">{prompt.title}</h2>
      </div>
      <p className="text-gray-600 mb-6">{prompt.promptText}</p>

      <div className="space-y-4">
        {prompt.inputs.map((input) => (
          <div key={input.name}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {input.name}
              {input.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <p className="text-sm text-gray-500 mb-2">{input.description}</p>
            {renderInput(input)}
          </div>
        ))}
      </div>

      <div className="mt-6">
        <button
          onClick={handleSubmit}
          disabled={isLoading}
          className="w-full px-4 py-2 bg-yellow-400 text-gray-800 rounded-md hover:bg-yellow-500 flex items-center justify-center"
        >
          {isLoading ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-800"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Processing...
            </>
          ) : (
            "Run Tool"
          )}
        </button>
      </div>
    </div>
  );
};

export default PromptUsage;
